import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlerts();
  }, [filter]);

  const fetchAlerts = async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) {
      navigate('/orderlab-admin/login');
      return;
    }

    try {
      let url = `${BACKEND_URL}/api/superadmin/alerts`;
      if (filter !== 'all') url += `?resolved=${filter === 'resolved'}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      } else if (response.status === 401) {
        navigate('/orderlab-admin/login');
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (alertId) => {
    const token = localStorage.getItem('superadmin_token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/superadmin/alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Alerte résolue');
        fetchAlerts();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-50 text-red-700 border-red-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200'
    };
    return styles[severity] || styles.info;
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical') return <AlertTriangle className="w-5 h-5 text-red-600" />;
    if (severity === 'warning') return <Clock className="w-5 h-5 text-amber-600" />;
    return <AlertTriangle className="w-5 h-5 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold mb-2">Alertes</h1>
          <p className="text-muted-foreground">{alerts.length} alerte(s) au total</p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <div className="flex gap-2">
            {['all', 'unresolved', 'resolved'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'Toutes' : f === 'unresolved' ? 'Non résolues' : 'Résolues'}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucune alerte trouvée</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <Card key={alert.id} className="p-6 hover:shadow-md transition-shadow" data-testid={`alert-${alert.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="mt-1">
                    {getSeverityIcon(alert.severity)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={`${getSeverityBadge(alert.severity)} border`}>
                        {alert.severity.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">{alert.type.replace(/_/g, ' ')}</Badge>
                      {alert.resolved && (
                        <Badge className="bg-green-50 text-green-700 border-green-200">
                          ✓ Résolue
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium mb-1">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(alert.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
                {!alert.resolved && (
                  <Button
                    size="sm"
                    onClick={() => handleResolve(alert.id)}
                    data-testid={`resolve-${alert.id}`}
                  >
                    Marquer résolue
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
